import { DEFAULT_MODEL, type ProviderVendor } from '@/pages/dashboard/components/aiProviderConstants'
import {
  chatbotApi,
  type AssistantFormsContext,
  type AssistantCapacityContext,
  type AssistantSettingsContext,
  type AssistantGeocodingContext,
  type AssistantLibraryContext,
  type AssistantMonitoringContext,
  type AssistantScriptsContext,
  type AssistantSecurityContext,
  type AssistantSurface,
  type ChatAttachedFile,
  type ChatAttachment,
} from '@/services/api/domains/chatbot.api'
import { routeToPageKind } from '@/services/assistant/pageKind'
import { mapSessionMessagesForResume } from '@/services/assistant/resumeSessionMessages'
import { requestConfirmation } from '@/stores/assistant-confirm-store'
import { useMachine } from '@xstate/react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { InterpreterFrom, StateFrom } from 'xstate'

import { useAuth } from '../../../hooks/useAuth'
import { hasUnsavedWork } from '../../../hooks/useUnsavedWork'
import { chatbotMachine, type ChatbotContext } from '../../../machines/chatbotMachine'
import type { DashboardBridge } from '../Dashboard/DashboardBridge'
import {
  ALWAYS_AVAILABLE_SURFACE_ACTIONS,
  DASHBOARD_SURFACE_ACTIONS,
  FORMS_BUILDER_SURFACE_ACTIONS,
  getAssistantSurface,
  MAP_ELEMENT_SURFACE_ACTIONS,
  MAP_SURFACE_ACTIONS,
  SCRIPTS_SESSION_SURFACE_ACTIONS,
  type MapSurfaceContext,
} from './assistant-actions'
import { executeClientToolBatch } from './executeClientTool'
import { createForwardingBridge } from './forwarding-bridge'
import { useAssistantAttachments, type AssistantAttachments } from './useAssistantAttachments'

/**
 * The sitewide assistant's own interpretation of `chatbotMachine` — a SEPARATE
 * runtime from the dashboard `chatbot` element (`DashboardChatbot`). Interpreted
 * with `elementId: undefined` (there is no dashboard element to scope the
 * session to) and a `bridge` that is a `createForwardingBridge()` instance, NOT
 * a real `DashboardBridge` — this provider is mounted at the top of
 * `AppRoutes`, outside every per-dashboard `<DashboardBridgeProvider>`, so it
 * cannot hold one directly. The forwarding bridge resolves the CURRENT
 * dashboard (via the `assistant-actions` registry) at the moment each method is
 * called, and no-ops when none is registered, so `emitBridgeEvents`'s
 * `if (ctx.bridge)` guard is satisfied unconditionally and `dispatchToolToBridge`
 * lands its effect on whatever dashboard is actually on screen.
 *
 * Do NOT reuse `DashboardChatbot`'s context-collection path (the five sequential
 * `bridge.request(...)` calls at up to 500ms each) — a sitewide turn's `context`
 * stays `undefined` regardless of whether a dashboard is mounted; only `surface`
 * (and, on a dashboard, `surface.dashboardId`) rides the request.
 */

export interface SendSitewideMessageOptions {
  image?: { dataUrl: string }
  /** Pictures and PDFs from the composer's picker. */
  attachments?: ChatAttachment[]
  /** Library files the picker uploaded for this turn. */
  attachedFiles?: ChatAttachedFile[]
}

export interface AssistantContextValue {
  state: StateFrom<typeof chatbotMachine>
  send: InterpreterFrom<typeof chatbotMachine>['send']
  /**
   * Sends a sitewide chat turn. Builds and attaches the `surface` descriptor
   * — `{ kind: 'sitewide', route, pageKind }` — from the CURRENT route on
   * every call, via react-router's `useLocation` (this provider lives inside
   * `BrowserRouter`, unlike `DashboardChatbot`, which reads
   * `window.location.pathname` only because several of its specs render it
   * with no Router in the tree). `surface` rides top-level on the outgoing
   * request, a sibling of `context` — never nested inside it — so it can
   * never be dropped by `context`'s own presence-gating (see
   * `DashboardChatbot.assembleChatContext`, which returns `undefined` on
   * context-less pages).
   */
  sendMessage: (message: string, options?: SendSitewideMessageOptions) => void
  /**
   * The composer's attachments. Held here rather than in the panel because the panel unmounts when
   * the assistant closes, and that must neither drop picked files nor abort a Library upload.
   */
  attachments: AssistantAttachments
}

export const AssistantContext = createContext<AssistantContextValue | null>(null)

/** Throwing accessor — descendants of `<AssistantProvider>` only. */
export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext)

  if (!ctx) {
    throw new Error('useAssistant must be used inside <AssistantProvider>')
  }

  return ctx
}

const SITEWIDE_PROVIDER: ProviderVendor = 'claude'
const SITEWIDE_MODEL = DEFAULT_MODEL[SITEWIDE_PROVIDER]

/**
 * Build the `surface` descriptor (and, on a map page, the map `context`) for a
 * sitewide turn.
 *
 * Exported for test: the whole point of this function is WHAT it sends, and
 * asserting that through a rendered provider would test React, not the rule.
 *
 * A registered-but-not-ready map sends `availableActions` containing only
 * `navigate_to`. That withholds every map tool while leaving server tools
 * (`list_layers`) untouched, because the server narrows only the client-tool
 * subset — and `navigate_to` is route-based, so it works even before the map
 * is ready (see `ALWAYS_AVAILABLE_SURFACE_ACTIONS`).
 */
export function buildSitewideSurface(route: string): {
  surface: Extract<AssistantSurface, { kind: 'sitewide' }>
  context?:
    | MapSurfaceContext
    | { library: AssistantLibraryContext }
    | { geocoding: AssistantGeocodingContext }
    | { forms: AssistantFormsContext }
    | { scripts: AssistantScriptsContext }
    | { security: AssistantSecurityContext }
    | { monitoring: AssistantMonitoringContext }
    | { capacity: AssistantCapacityContext }
    | { settings: AssistantSettingsContext }
} {
  const base = { kind: 'sitewide' as const, route, pageKind: routeToPageKind(route) }
  const registered = getAssistantSurface()

  if (!registered) return { surface: base, context: undefined }

  if (registered.kind === 'map') {
    const context = registered.readContext()

    return {
      surface: {
        ...base,
        availableActions: context
          ? [...ALWAYS_AVAILABLE_SURFACE_ACTIONS, ...MAP_SURFACE_ACTIONS]
          : [...ALWAYS_AVAILABLE_SURFACE_ACTIONS],
      },
      context: context ?? undefined,
    }
  }

  if (registered.kind === 'scripts') {
    // A script page adds the live-notebook tools only while its notebook's bridge is ready.
    return {
      surface: {
        ...base,
        availableActions: [
          ...ALWAYS_AVAILABLE_SURFACE_ACTIONS,
          ...(registered.bridge?.ready ? SCRIPTS_SESSION_SURFACE_ACTIONS : []),
        ],
      },
      context: registered.readContext(),
    }
  }

  if (registered.kind === 'library' ||
    registered.kind === 'geocoding' ||
    registered.kind === 'security' ||
    registered.kind === 'monitoring' ||
    registered.kind === 'capacity' ||
    registered.kind === 'settings'
  ) {
    // None of these pages has page actions in this phase: claim only the
    // always-available tools, or this page would strip `navigate_to`.
    return {
      surface: { ...base, availableActions: [...ALWAYS_AVAILABLE_SURFACE_ACTIONS] },
      context: registered.readContext(),
    }
  }

  if (registered.kind === 'forms') {
    // The builder adds `edit_form` only when it registered `applyEdit`; the list and the
    // submissions page claim the always-available tools alone.
    return {
      surface: {
        ...base,
        availableActions: [
          ...ALWAYS_AVAILABLE_SURFACE_ACTIONS,
          ...(registered.applyEdit ? FORMS_BUILDER_SURFACE_ACTIONS : []),
        ],
      },
      context: registered.readContext(),
    }
  }

  // A dashboard. `availableActions` is computed from what is MOUNTED, read at
  // send time — so the gate reflects the page as it is now, not as it was when
  // the dashboard loaded. No `context`: the map context shape describes the
  // /map engine, and a dashboard's data reaches the model through the server
  // reads instead.
  if (registered.kind !== 'dashboard') return { surface: base, context: undefined }

  const elementTypes = registered.elementTypes()
  const availableActions: string[] = [...ALWAYS_AVAILABLE_SURFACE_ACTIONS, ...DASHBOARD_SURFACE_ACTIONS]

  if (elementTypes.includes('map')) availableActions.push(...MAP_ELEMENT_SURFACE_ACTIONS)

  return {
    surface: { ...base, dashboardId: registered.dashboardId, availableActions },
    context: undefined,
  }
}

export function AssistantProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { authState, user } = useAuth()

  // The AUTHENTICATED IDENTITY this provider's resume effect is keyed on.
  // `authState` alone can't carry an identity (it only distinguishes
  // "authenticated" from every other state), and `user` alone races ahead of
  // verification: `useAuth`'s initial render can already hold a stale
  // `user` object hydrated straight from localStorage while `authState` is
  // still `'loading'` (checkAuth() hasn't confirmed the session yet). Gating
  // `user.id` on `authState === 'authenticated'` gives exactly the three
  // states the resume effect below needs to tell apart: "not yet known"
  // (loading/unauthenticated/tenant_* → null, no fetch), "user X", and
  // "user Y" (a different id → the effect below treats this as an identity
  // CHANGE and clears the machine first).
  const identityKey = authState === 'authenticated' && user ? user.id : null
  // What may be attached follows the caller's role; anyone not (yet) verified attaches as a guest.
  const attachments = useAssistantAttachments(authState === 'authenticated' && user?.role ? user.role : 'guest')
  const clearAttachments = attachments.clear

  // One forwarding bridge per provider mount, created before the machine is
  // interpreted so `ctx.bridge` never changes identity. See
  // `forwarding-bridge.ts` for why the machine cannot simply be handed the
  // dashboard's own bridge.
  const bridgeRef = useRef<DashboardBridge | null>(null)
  if (!bridgeRef.current) bridgeRef.current = createForwardingBridge()

  const [state, send] = useMachine(chatbotMachine, {
    context: {
      elementId: undefined,
      sessionId: null,
      messages: [],
      pendingUserMessage: null,
      pendingToolResults: null,
      runId: null,
      deferredToolResult: null,
      clientToolRequest: null,
      toolResultOutstanding: false,
      pendingContext: undefined,
      pendingSurface: undefined,
      activeStill: null,
      pendingImage: null,
      currentResponse: '',
      currentToolCalls: [],
      currentRoundNumber: null,
      streamingEnabled: (import.meta.env?.VITE_AI_CHATBOT_STREAMING ?? '1') !== '0',
      streamingRetryUsed: false,
      error: null,
      usage: null,
      bridge: bridgeRef.current,
      api: chatbotApi,
      provider: SITEWIDE_PROVIDER,
      model: SITEWIDE_MODEL,
    } as Partial<ChatbotContext>,
  })

  // Resume the sitewide conversation once the AUTHENTICATED IDENTITY is
  // known. Deliberately NOT a copy of DashboardChatbot's own per-element
  // resume effect:
  //
  // 1. No `resumed.current = true`-before-the-request latch. That latch is
  //    correct for a per-element component that mounts fresh once per
  //    dashboard element instance, but this provider is a long-lived,
  //    once-mounted-for-the-app-session runtime (`routes/index.tsx` mounts
  //    it once, above `<Routes>`, and it never unmounts across client-side
  //    navigation) — a latch that can never re-run would be dead weight
  //    here, not a safety guard. A plain `cancelled` flag (for the
  //    unmount-during-fetch case) is all this needs.
  // 2. No `if (anon) return` early return. DashboardChatbot needs one
  //    because IT can be embedded on a PUBLIC dashboard a guest views
  //    anonymously. This provider's visible surface (`AssistantSurface`)
  //    only ever renders inside `<AppLayout>`, which sits behind
  //    `AuthGuard` — a guest never sees it.
  //
  // Keyed on `identityKey` (see above), NOT `send` — `send` is
  // `service.send`, assigned ONCE as an own property by xstate's Interpreter
  // constructor (`useConstant` under `useMachine`), so a `[send]` dependency
  // never re-fires: the effect ran exactly once per page load, before
  // authentication could possibly have resolved, and never again across a
  // same-tab account switch. Keying on `identityKey` instead means:
  //   - it does NOT fetch while unauthenticated / not-yet-known
  //     (`identityKey` is `null`, the effect body below no-ops);
  //   - it fetches exactly when an identity newly becomes known;
  //   - when the identity CHANGES (a same-tab sign-out + a different
  //     account signing in, with no reload in between — `useAuth.signOut`
  //     clears localStorage and flips state without unmounting this
  //     provider), it sends `CLEAR` — which also nulls `sessionId`, unlike
  //     `setError` — BEFORE resuming the new identity's session, so the
  //     previous user's messages[] and sessionId can never leak onto the
  //     next one.
  const lastResumedIdentityRef = useRef<string | null>(null)

  useEffect(() => {
    if (!identityKey) {
      // Not authenticated (or not yet known — still verifying). Leave
      // `lastResumedIdentityRef` untouched: if user A signs out (identityKey
      // → null, this branch, no-op) and user B then signs in without a
      // reload, the effect below still compares B's id against A's — the
      // null in between must not erase that memory or the leak this exists
      // to prevent would go undetected.
      return
    }

    if (lastResumedIdentityRef.current && lastResumedIdentityRef.current !== identityKey) {
      send({ type: 'CLEAR' })
      // The previous user's picked files must not ride the next user's first message.
      clearAttachments()
    }

    lastResumedIdentityRef.current = identityKey

    let cancelled = false

    chatbotApi
      .getActiveSession({ surface: 'sitewide' })
      .then(resp => {
        if (cancelled) return
        const session = resp.data
        if (!session?._id) return

        send({
          type: 'RESUME_SESSION',
          sessionId: session._id,
          messages: mapSessionMessagesForResume(session.messages),
        })
      })
      .catch(() => {
        // 404 = no prior session for this identity; anything else — treat
        // it the same way and start fresh rather than risk showing stale
        // (or another identity's) state.
      })

    return () => {
      cancelled = true
    }
  }, [identityKey, send, clearAttachments])

  // Task 17 — the browser half of a client tool. The machine parks a
  // `client_tool_request` frame in `clientToolRequest` (it cannot execute one
  // itself: that needs the router, the page's unsaved-work state and the
  // signed-in user's entitlements); this effect runs EVERY call in the batch
  // and dispatches exactly ONE `SEND_TOOL_RESULT` covering all of them. The
  // server requires every pending call answered exactly once and rejects
  // duplicate toolCallIds, so a partial or repeated batch dead-ends the turn.
  //
  // Dispatching as soon as the tools resolve is safe even mid-stream: the
  // machine DEFERS a `SEND_TOOL_RESULT` that lands while `active` and re-fires
  // it on reaching `ready` — which exists precisely because
  // `client_tool_request` and `message_complete` need not share an SSE chunk.
  const clientToolRequest = state.context.clientToolRequest

  // Dedup is keyed on the PARKED REQUEST OBJECT, never on `runId`. A runId is
  // per-RUN, not per-REQUEST: the server reuses `session.activeRun.runId`
  // across continuations and stamps every `client_tool_request` with it, so a
  // second `navigate_to` in the same run (multi-step navigation, or a retry
  // after a refusal — both behaviours the tool description invites) carries
  // the SAME runId. A `Set<runId>` would swallow it and the run would hang
  // forever waiting for a result that was never sent. `storeClientToolRequest`
  // assigns a fresh object per frame, so object identity is per-REQUEST, while
  // a double render (StrictMode, re-render) still sees the same object and
  // skips — idempotent where it must be, live where it must be.
  const handledRequestsRef = useRef<WeakSet<object>>(new WeakSet())

  // Live refs: the executor deps must reflect the CURRENT route and user at the
  // moment the tool runs, without making this effect re-fire (and re-execute a
  // navigation) on every route change.
  const locationRef = useRef(location.pathname)
  locationRef.current = location.pathname
  const searchRef = useRef(location.search)
  searchRef.current = location.search
  const userRef = useRef(user)
  userRef.current = user
  // `navigate` MUST be behind a ref and MUST NOT be an effect dependency.
  // Under `<BrowserRouter>` `useNavigate()` memoises its callback on the
  // current pathname, so a SUCCESSFUL navigation changes its identity — an
  // effect keyed on it would tear down and re-run mid-flight, and the send
  // would be lost. There is also no `cancelled` gate on the send below, for
  // the same reason: the dedup above already makes it exactly-once, and a
  // cancelled send is a permanently suspended server run.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  // Task 20 Fix 1(b): the CURRENT parked request, read at the moment the batch
  // resolves. `CANCEL` (Stop) — and every error/clear path — now clears
  // `clientToolRequest`, so an execution already in flight when the user
  // pressed Stop finds a different (usually null) value here and drops its
  // `SEND_TOOL_RESULT` on the floor instead of starting a NEW BILLED
  // continuation turn the user explicitly asked not to happen. Identity
  // comparison, matching `handledRequestsRef`'s per-REQUEST-object idiom: a
  // second `navigate_to` in the same run is a different object, so a genuine
  // follow-up request is never mistaken for the cancelled one.
  const currentClientToolRequestRef = useRef(clientToolRequest)
  currentClientToolRequestRef.current = clientToolRequest

  useEffect(() => {
    if (!clientToolRequest) return
    if (handledRequestsRef.current.has(clientToolRequest)) return
    handledRequestsRef.current.add(clientToolRequest)

    const { runId, toolCalls } = clientToolRequest

    void executeClientToolBatch(toolCalls, {
      navigate: (route: string, options?: { replace?: boolean }) => navigateRef.current(route, options),
      hasUnsavedWork,
      currentRoute: () => locationRef.current,
      currentSearch: () => searchRef.current,
      caller: {
        role: userRef.current?.role ?? 'viewer',
        // `useAuth` carries a central operator as an EMPTY tenantId string,
        // where the manifest's `centralOnly` rule reads null/undefined —
        // normalise so a central operator isn't refused their own routes.
        tenantId: userRef.current?.tenantId ? userRef.current.tenantId : null,
        assignedModules: userRef.current?.assignedModules ?? [],
      },
      // The prompt renders in `AssistantSurface` and this promise resolves on
      // the click. The batch executor awaits it, so the parked run simply stays
      // parked while the user thinks — the Lambda returned long ago.
      confirm: requestConfirmation,
    })
      .then(toolResults => {
        if (currentClientToolRequestRef.current !== clientToolRequest) return
        send({ type: 'SEND_TOOL_RESULT', runId, toolResults })
      })
      .catch((err: unknown) => {
        // `executeClientToolBatch` resolves per-call errors, but the batch
        // itself can still reject on a malformed frame (a non-iterable
        // `toolCalls`). An unhandled rejection there is a run suspended
        // forever, so answer every call we can name with an error result.
        const message = err instanceof Error ? err.message : String(err)
        const toolResults = (Array.isArray(toolCalls) ? toolCalls : []).map(call => ({
          toolCallId: call.id,
          result: { error: message },
        }))

        if (currentClientToolRequestRef.current !== clientToolRequest) return
        send({ type: 'SEND_TOOL_RESULT', runId, toolResults })
      })
  }, [clientToolRequest, send])

  const sendMessage = useCallback(
    (message: string, options?: SendSitewideMessageOptions) => {
      const { surface, context } = buildSitewideSurface(location.pathname)

      send({
        type: 'SEND_MESSAGE',
        message,
        surface,
        ...(context ? { context } : {}),
        ...(options?.image ? { image: options.image } : {}),
        ...(options?.attachments?.length ? { attachments: options.attachments } : {}),
        ...(options?.attachedFiles?.length ? { attachedFiles: options.attachedFiles } : {}),
      })
    },
    [location.pathname, send],
  )

  const value = useMemo<AssistantContextValue>(
    () => ({ state, send, sendMessage, attachments }),
    [state, send, sendMessage, attachments],
  )

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>
}
